/**
 * Issue #1845 — humanize the backend `(unknown policy)` sentinel for the
 * signer-facing UI.
 *
 * The backend `getSignerAssignments` / `processAdminDigest` paths substitute
 * the literal string `(unknown policy)` as the title when the underlying
 * policy can no longer be loaded (e.g., the policy was deleted between
 * assignment and render). That sentinel is stable on purpose — admin digest
 * sorting and audit messages rely on it — so it is left alone on the wire.
 *
 * This helper replaces it with friendly user copy on render. Callers should
 * also pair this with {@link isMissingPolicy} to tweak the visual treatment
 * (italic / muted) and disable actions that wouldn't make sense for a
 * deleted policy (e.g., "View Document").
 */

const MISSING_POLICY_SENTINEL = '(unknown policy)';
const MISSING_POLICY_FALLBACK = 'Policy no longer available';

/**
 * Returns a user-facing policy title. If the raw title is the backend
 * sentinel `(unknown policy)` or otherwise empty, returns the friendly
 * fallback `Policy no longer available`. Otherwise returns the raw title
 * unchanged.
 *
 * Matches the sentinel by exact equality, not substring — a real title that
 * happens to contain the phrase is preserved verbatim.
 */
export function displayPolicyTitle(raw: string | undefined | null): string {
    if (!raw || raw === MISSING_POLICY_SENTINEL) {
        return MISSING_POLICY_FALLBACK;
    }
    return raw;
}

/**
 * Returns true when the assignment's `policyTitle` corresponds to a missing
 * (deleted / unresolvable) policy. Use this to apply italic / muted styling
 * to the title and disable actions that depend on the underlying policy
 * existing (e.g., "View Document").
 */
export function isMissingPolicy(raw: string | undefined | null): boolean {
    return !raw || raw === MISSING_POLICY_SENTINEL;
}

/**
 * Issue #1862 — strip a trailing 10+ digit numeric token from a policy
 * title. Title shapes seen in the field include `QA Signer Policy
 * 1776962912874` — a 13-digit ms-timestamp left in the title by an upload
 * accident. A 9-digit suffix stays in place because that could be a
 * legitimate identifier; 10+ digits is overwhelmingly a paste artifact.
 *
 * The regex requires whitespace before the digits, so titles like
 * "Policy2024" or "Acceptable Use Policy 2026" are untouched. The `|| raw`
 * fallback guarantees we never collapse a title to an empty string when the
 * entire title is numeric.
 *
 * Pair with {@link displayPolicyTitle} — call `cleanPolicyTitle` FIRST so
 * cleaning runs on the raw title, then `displayPolicyTitle` handles the
 * missing-policy sentinel separately.
 *
 * Shared with `components/dashboard/TimeToSignChart.tsx` (#1863).
 */
export function cleanPolicyTitle(raw: string | undefined | null): string {
    if (!raw) return raw ?? '';
    return raw.replace(/\s+\d{10,}\s*$/, '').trim() || raw;
}
